// 1. React
import { memo, useCallback, useState } from "react";
import ReactDOM from "react-dom";

// 2. Helpers
import { useTranslate } from "helpers";

// 3. Types
import { InvoiceItemsProps } from "./types";
import { Errors } from "services/forms/validation/types";
import { InvoiceOutgoingItemEditFormValues } from "services/api/fleet/vehicles/invoices/types";

// 4. API / Consts
import { initialFleetVehicleInvoiceItemEditForm } from "services/api/fleet/vehicles/invoices/consts";
import { transformInvoiceItemRow, transformInvoiceItemRowToForm } from "services/api/fleet/vehicles/invoices/transformations";

// 5. UI Components
import InvoiceItemsAdded from "./ItemsAdded";
import { InvoiceItemForm } from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleInvoiceSaleEditCard/components/InvoiceItemForm";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { Button } from "ui/components/Button/Button";

// 6. Validation
import { validateYup } from "services/forms/validation/validation";
import { FLEET_VEHICLE_INVOICE_ITEM_EDIT_FORM_SCHEMA } from "./validation";
import { Dialog } from "ui/components/Dialog";


const InvoiceItems: React.FC<InvoiceItemsProps> = (props) => {
    const { formValues, setFormValues } = props;

    const { t } = useTranslate();

    const [itemFormAdd, setItemFormAdd] =
        useState<InvoiceOutgoingItemEditFormValues>(
            { ...initialFleetVehicleInvoiceItemEditForm }
        );

    const [itemId, setItemId] = useState<string>("");
    const [isEdit, setIsEdit] = useState(false);
    const [showItemFormModal, setShowItemFormModal] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const handleExpandToggle = useCallback(() => {
        setIsExpanded(previous => !previous);
    }, []);
    const [errors, setErrors] = useState<Errors>({});
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleAddItem = async (e: any) => {
        e.preventDefault();

        const isValid = validateYup(
            itemFormAdd,
            FLEET_VEHICLE_INVOICE_ITEM_EDIT_FORM_SCHEMA,
            setErrors
        );

        if (!isValid) {
            return;
        }

        setIsLoading(true);

        if (isEdit) {
            setFormValues(prev => ({
                ...prev,
                items: prev.items.map(item =>
                    item.id === itemFormAdd.id
                        ? transformInvoiceItemRow(itemFormAdd)
                        : item
                ),
            }));
        }
        else {
            setFormValues(prev => ({
                ...prev,
                items: [...prev.items, transformInvoiceItemRow(itemFormAdd)]
            }));
        }

        setItemFormAdd(initialFleetVehicleInvoiceItemEditForm);
        setIsEdit(false);
        setItemId("");
        setIsLoading(false);
        setShowItemFormModal(false);
    };

    const handleAddClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        setItemFormAdd({ ...initialFleetVehicleInvoiceItemEditForm, id: crypto.randomUUID() });
        setItemId("");
        setIsEdit(false);
        setErrors({});
        setShowItemFormModal(true);
    };

    const handleEditClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        event.preventDefault();

        let item = formValues.items.find(item => item.id === id)

        if (!item)
            return;

        setItemFormAdd(transformInvoiceItemRowToForm(item));
        setItemId(id);
        setIsEdit(true);
        setShowItemFormModal(true);
    }

    const handleItemFormModalClose = () => {
        setShowItemFormModal(false);
        setItemFormAdd(initialFleetVehicleInvoiceItemEditForm);
        setIsEdit(false);
        setItemId("");
        setErrors({});
    };

    const handleDeleteClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, id: string) => {
        event.stopPropagation();
        event.preventDefault();
        setItemId(id);
        setOpenDialog(true);
    }

    const handleDeleteDialogConfirm = useCallback(async () => {
        if (!itemId) return;

        setFormValues(prev => ({
            ...prev,
            items: prev.items.map(item =>
                item.id === itemId
                    ? { ...item, deleted: true }
                    : item
            ),
        }));

        setOpenDialog(false);
        setItemId("");

    }, [itemId, t]);

    const handleDeleteDialogClose = useCallback(() => {
        setItemId("");
        setOpenDialog(false);
    }, []);


    const items = formValues.items.filter(i => !i.deleted);

    const renderItems = () => (
        <div className="p-3">
            <div className="flex justify-end items-center gap-2 mb-3">
                <Button
                    variant="outlined"
                    size="small"
                    startIcon="/media/icons/plus.svg"
                    onClick={handleAddClick}
                >
                    Dodaj stavku
                </Button>

                {/* Jedna stavka se vidi i bez razvlačenja, pa se gumb nudi tek kad ih ima više. */}
                {items.length > 1 && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon="/media/icons/scaling.svg"
                        onClick={handleExpandToggle}
                    />
                )}
            </div>
            {items && items.length > 0 && (
                <InvoiceItemsAdded
                    isLoading={false}
                    items={items}
                    onEditClick={handleEditClick}
                    onDeleteClick={handleDeleteClick}
                />
            )}
        </div>
    );

    return (
        <>
            {isExpanded ? (
                // Razvučeni popis ide u portal: `fixed` unutar modala bi ga odrezao obrazac, koji
                // se skrola. Zauzima desnu polovicu, da prikaz s lijeve strane ostane vidljiv.
                ReactDOM.createPortal(
                    <div className="fixed top-0 bottom-0 left-0 lg:left-1/2 right-0 z-[60] bg-white border-l border-slate-200 flex flex-col">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                            <span className="text-sm font-medium text-sky-600">Stavke računa</span>
                            <Button
                                variant="outlined"
                                size="small"
                                children={t("admin.close")}
                                onClick={handleExpandToggle}
                            />
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto">{renderItems()}</div>
                    </div>,
                    document.body
                )
            ) : (
                renderItems()
            )}

            <Modal
                show={showItemFormModal}
                onHide={handleItemFormModalClose}
                position="centered" size="medium">
                <ModalHeader
                    icon="/media/images/add-icon.svg"
                    title={isEdit
                        ? t("admin.fleet.vehicle.invoice.item.edit.title")
                        : t("admin.fleet.vehicle.invoice.item.add.title")
                    }
                    onHide={handleItemFormModalClose}
                />
                <ModalBody>
                    {showItemFormModal && (
                        <>
                            <InvoiceItemForm
                                itemFormAdd={itemFormAdd}
                                setItemFormAdd={setItemFormAdd}
                                errors={errors}
                                isEdit={isEdit}
                                onSubmit={handleAddItem}
                            />
                            <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                                <Button
                                    variant="outlined"
                                    children={t("admin.close")}
                                    onClick={handleItemFormModalClose}
                                />
                                <Button
                                    variant="primary"
                                    children={isEdit ? t("admin.save") : t("admin.fleet.vehicle.invoice.item.add.button")}
                                    onClick={handleAddItem}
                                />
                            </div>
                        </>
                    )}
                </ModalBody>
            </Modal>
            <Dialog
                open={openDialog}
                title={t(
                    "admin.fleet.vehicle.invoice.item.dialog.delete.title"
                )}
                message={t(
                    "admin.fleet.vehicle.invoice.item.dialog.delete.message"
                )}
                confirmText={t(
                    "admin.fleet.vehicle.invoice.item.dialog.delete.confirmtext"
                )}
                cancelText={t(
                    "admin.fleet.vehicle.invoice.item.dialog.delete.canceltext"
                )}
                onConfirm={handleDeleteDialogConfirm}
                onCancel={handleDeleteDialogClose}
            />
        </>
    );
};

export default memo(InvoiceItems);
